import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
  Fragment,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import underscore from "underscore";

import {
  fetchAiSettings,
  updateAiSettings,
  fetchGoogleCalendars,
  updateGoogleCalendarName,
  deleteGoogleCalendar,
  disconnectGoogleCalendar,
} from "../../../redux/action";

import { apiCall } from "../../../functions/utils";

import {
  Box,
  Card,
  CardHeader,
  CardBody,
  Switch,
  FormControl,
  FormLabel,
  Select,
  Input,
  Textarea,
  Button,
  Stack,
  HStack,
  Alert,
  AlertIcon,
  AlertTitle,
  Spinner,
} from "@chakra-ui/react";
import { TIMEZONES } from "../../../helpers/Utils";
export default function AiAssistant() {
  const dispatch = useDispatch();
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState(false);

  const [disconnectLoadingMap, setDisconnectLoadingMap] = useState({});
  const [calAuthLoading, setCalAuthLoading] = useState(false);

  const settings = useSelector((state) => state.settings);
  const activeAccount = useSelector((state) => state.authUser?.activeAccount);

  const isLoading = settings.is_loading && !saving;

  const [localNameMap, setLocalNameMap] = useState({});
  const [calLoadingMap, setCalLoadingMap] = useState({});

  const [formState, setFormState] = useState({
    is_ai_enabled: false,
    business_name: "",
    business_hours: "",
    booking_link: "",
    faq_context: "",
    review_link: "",
    time_zone: "",
    stop_rules_json: {
      handoff_keywords: [],
      max_turns_before_handoff: 3,
    },
  });

  const originalRef = useRef(null);

  const checkCalendarConnection = useCallback(() => {
    dispatch(fetchGoogleCalendars());
  }, [dispatch]);

  const connectGoogleCalendar = useCallback(async () => {
    try {
      setCalAuthLoading(true);
      const redirectBack = window.location.origin + window.location.pathname;

      const res = await apiCall(
        true,
        "GET",
        `/api/google/connect?redirect=${encodeURIComponent(
          redirectBack
        )}&mode=json`
      );

      const authUrl = res?.data?.authUrl || res?.data?.url || res?.data;
      if (!authUrl) throw new Error("Auth URL not returned by server");

      window.location.href = authUrl;
    } catch (e) {
      console.error("Failed to start Google OAuth:", e);
    } finally {
      setCalAuthLoading(false);
    }
  }, []);

  useEffect(() => {
    dispatch(fetchAiSettings());
    dispatch(fetchGoogleCalendars());
  }, [activeAccount, dispatch]);

  useEffect(() => {
    if (!settings.ai_settings) return;

    const s = settings.ai_settings;

    const normalized = {
      is_ai_enabled: s.is_ai_enabled ?? false,
      business_name: s.business_name || "",
      business_hours: s.business_hours || "",
      booking_link: s.booking_link || "",
      review_link: s.review_link || "",
      time_zone: s.time_zone || "",
      faq_context: s.faq_context || "",
      stop_rules_json: {
        handoff_keywords: Array.isArray(s.stop_rules_json?.handoff_keywords)
          ? s.stop_rules_json.handoff_keywords
          : [],
        max_turns_before_handoff:
          s.stop_rules_json?.max_turns_before_handoff ?? 3,
      },
    };

    if (originalRef.current === null) originalRef.current = normalized;
    if (!touched) setFormState(normalized);
  }, [settings.ai_settings, touched]);

  const handleFieldChange = useCallback((name, value) => {
    setTouched(true);
    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const disconnectGoogleCalendarHandler = useCallback(
    async (connectionId) => {
      setDisconnectLoadingMap((prev) => ({
        ...prev,
        [connectionId]: true,
      }));

      try {
        await dispatch(disconnectGoogleCalendar(connectionId));
      } finally {
        setDisconnectLoadingMap((prev) => ({
          ...prev,
          [connectionId]: false,
        }));
      }
    },
    [dispatch]
  );

  const updateFriendlyName = useCallback(
    (connectionId, calendarId, friendlyName) => {
      console.log("Updating friendly name...", {
        connectionId,
        calendarId,
        friendlyName,
      });
      return dispatch(
        updateGoogleCalendarName(connectionId, calendarId, friendlyName)
      );
    },
    [dispatch]
  );

  const deleteCalendar = useCallback(
    (connectionId, calendarId) => {
      return dispatch(deleteGoogleCalendar(connectionId, calendarId));
    },
    [dispatch]
  );

  const handleStopRulesChange = useCallback((name, value) => {
    setTouched(true);
    setFormState((prev) => ({
      ...prev,
      stop_rules_json: {
        ...prev.stop_rules_json,
        [name]: value,
      },
    }));
  }, []);

  const isDirty = useMemo(() => {
    if (!originalRef.current) return false;
    return !underscore.isEqual(originalRef.current, formState);
  }, [formState]);

  const handleSave = useCallback(async () => {
    setSaving(true);

    const trimmedBusinessName = (formState.business_name || "").trim();
    const trimmedBusinessHours = (formState.business_hours || "").trim();
    const trimmedBookingLink = (formState.booking_link || "").trim();
    const trimmedFaqContext = (formState.faq_context || "").trim();
    const trimmedReviewLink = (formState.review_link || "").trim();

    const normalizedMaxTurns =
      Number(formState.stop_rules_json.max_turns_before_handoff) || 3;
    const normalizedTimeZone = formState.time_zone || null;

    await dispatch(
      updateAiSettings({
        is_ai_enabled: !!formState.is_ai_enabled,
        business_name: trimmedBusinessName,
        business_hours: trimmedBusinessHours,
        booking_link: trimmedBookingLink,
        faq_context: trimmedFaqContext,
        review_link: trimmedReviewLink,
        time_zone: normalizedTimeZone,
        stop_rules_json: {
          handoff_keywords: formState.stop_rules_json.handoff_keywords,
          max_turns_before_handoff: normalizedMaxTurns,
        },
      })
    );

    // Update reference for dirty tracking
    originalRef.current = {
      ...formState,
      business_name: trimmedBusinessName,
      business_hours: trimmedBusinessHours,
      booking_link: trimmedBookingLink,
      faq_context: trimmedFaqContext,
      review_link: trimmedReviewLink,
      time_zone: normalizedTimeZone,
      stop_rules_json: {
        ...formState.stop_rules_json,
        max_turns_before_handoff: normalizedMaxTurns,
      },
    };

    setTouched(false);
    setSaving(false);
  }, [dispatch, formState]);

  const handoffKeywordsString =
    formState.stop_rules_json.handoff_keywords.join(", ");

  const onKeywordsChange = useCallback(
    (val) => {
      const arr = val
        .split(",")
        .map((w) => w.trim())
        .filter((w) => w.length > 0);

      handleStopRulesChange("handoff_keywords", arr);
    },
    [handleStopRulesChange]
  );

  return (
    <Fragment>
      <Box maxH="calc(100vh - 150px)" overflowY="auto" px="10px" py="10px">
        {isLoading && (
          <Box
            w="100%"
            py="40px"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <Spinner size="lg" color="blue.500" />
          </Box>
        )}

        {!isLoading && (
          <Card className="dashboard-link-list" mt="24px">
            <CardHeader
              textTransform="capitalize"
              fontSize="20px"
              fontWeight="600"
            >
              AI Assistant Settings
            </CardHeader>

            <CardBody>
              <Stack spacing={8}>
                {/* SECTION: Core toggle and identity */}
                <Stack spacing={6}>
                  {/* Enable / Disable AI */}
                  <FormControl display="flex" alignItems="center">
                    <FormLabel
                      htmlFor="is_ai_enabled"
                      mb="0"
                      fontWeight="500"
                      fontSize="16px"
                      color="#4a5568"
                    >
                      Enable AI Assistant
                    </FormLabel>

                    <Switch
                      id="is_ai_enabled"
                      isChecked={formState.is_ai_enabled}
                      onChange={(e) =>
                        handleFieldChange("is_ai_enabled", e.target.checked)
                      }
                    />
                  </FormControl>

                  {/* GOOGLE CALENDAR CONNECTION */}
                  <Stack spacing={4} px="24px" pt="8px">
                    <Box fontWeight="600" fontSize="16px" color="#2d3748">
                      Calendar Connections
                    </Box>

                    {settings.calendars_loading ? (
                      <HStack>
                        <Spinner size="sm" />
                        <Box fontSize="14px" color="#718096">
                          Checking calendar connections…
                        </Box>
                      </HStack>
                    ) : settings.googleConnections?.length > 0 ? (
                      <Stack spacing={6}>
                        {settings.googleConnections.map((conn) => (
                          <Box
                            key={conn.connectionId}
                            p="16px"
                            border="1px solid #E2E8F0"
                            borderRadius="md"
                            bg="gray.50"
                          >
                            {/* Header */}
                            <Alert status="success" borderRadius="md" mb={4}>
                              <AlertIcon />
                              <Box>
                                <AlertTitle mb={1}>
                                  Google account connected
                                </AlertTitle>
                                <Box fontSize="sm" color="#2d3748">
                                  Connected as{" "}
                                  <strong>{conn.google_email}</strong>
                                </Box>
                              </Box>
                            </Alert>

                            {/* Calendars inside this connection */}
                            <Box fontSize="13px" color="#4a5568" mb={2}>
                              Calendars synced from this Google account:
                            </Box>

                            <Stack pl="4" spacing={4}>
                              {conn.calendars.map((cal) => {
                                const currentName =
                                  localNameMap[cal.calendarId] ??
                                  cal.friendly_name;

                                return (
                                  <Box
                                    key={cal.calendarId}
                                    p="10px"
                                    border="1px solid #E2E8F0"
                                    borderRadius="md"
                                    bg="white"
                                  >
                                    {/* Calendar Name */}
                                    <HStack justify="space-between" mb={2}>
                                      <Box fontSize="12px" color="gray.500">
                                        Calendar:{" "}
                                        <strong>{cal.google_name}</strong>
                                      </Box>

                                      {cal.primary && (
                                        <Box
                                          as="span"
                                          px="6px"
                                          py="1px"
                                          borderRadius="full"
                                          fontSize="11px"
                                          bg="green.50"
                                          color="green.600"
                                        >
                                          Primary
                                        </Box>
                                      )}
                                    </HStack>

                                    {/* Friendly Name Input */}
                                    <FormControl>
                                      <FormLabel fontSize="13px" mb="4px">
                                        Friendly Name
                                      </FormLabel>
                                      <Input
                                        size="sm"
                                        value={currentName}
                                        onChange={(e) =>
                                          setLocalNameMap((prev) => ({
                                            ...prev,
                                            [cal.calendarId]: e.target.value,
                                          }))
                                        }
                                      />
                                    </FormControl>

                                    {/* Save + Delete */}
                                    <HStack mt={3}>
                                      <Button
                                        size="xs"
                                        colorScheme="blue"
                                        isLoading={
                                          calLoadingMap[cal.calendarId]?.saving
                                        }
                                        isDisabled={
                                          calLoadingMap[cal.calendarId]
                                            ?.saving ||
                                          calLoadingMap[cal.calendarId]
                                            ?.deleting
                                        }
                                        onClick={() => {
                                          setCalLoadingMap((prev) => ({
                                            ...prev,
                                            [cal.calendarId]: {
                                              saving: true,
                                              deleting: false,
                                            },
                                          }));

                                          updateFriendlyName(
                                            conn.connectionId,
                                            cal.calendarId,
                                            currentName
                                          ).finally(() => {
                                            setCalLoadingMap((prev) => ({
                                              ...prev,
                                              [cal.calendarId]: {
                                                saving: false,
                                                deleting: false,
                                              },
                                            }));

                                            setLocalNameMap((prev) => {
                                              const next = { ...prev };
                                              delete next[cal.calendarId];
                                              return next;
                                            });
                                          });
                                        }}
                                      >
                                        Save
                                      </Button>

                                      <Button
                                        size="xs"
                                        colorScheme="red"
                                        variant="outline"
                                        isLoading={
                                          calLoadingMap[cal.calendarId]
                                            ?.deleting
                                        }
                                        isDisabled={
                                          calLoadingMap[cal.calendarId]
                                            ?.deleting ||
                                          calLoadingMap[cal.calendarId]?.saving
                                        }
                                        onClick={() => {
                                          setCalLoadingMap((prev) => ({
                                            ...prev,
                                            [cal.calendarId]: {
                                              saving: false,
                                              deleting: true,
                                            },
                                          }));

                                          deleteCalendar(
                                            conn.connectionId,
                                            cal.calendarId
                                          ).finally(() =>
                                            setCalLoadingMap((prev) => ({
                                              ...prev,
                                              [cal.calendarId]: {
                                                saving: false,
                                                deleting: false,
                                              },
                                            }))
                                          );
                                        }}
                                      >
                                        Remove
                                      </Button>
                                    </HStack>
                                  </Box>
                                );
                              })}
                            </Stack>

                            {/* Disconnect this ONE Google account */}
                            <HStack pt={3}>
                              <Button
                                variant="outline"
                                colorScheme="red"
                                size="sm"
                                onClick={() =>
                                  disconnectGoogleCalendarHandler(
                                    conn.connectionId
                                  )
                                }
                                isLoading={
                                  !!disconnectLoadingMap[conn.connectionId]
                                }
                              >
                                Disconnect this account
                              </Button>

                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={checkCalendarConnection}
                                isDisabled={calAuthLoading}
                              >
                                Recheck
                              </Button>
                            </HStack>
                          </Box>
                        ))}

                        {/* Add new Google account */}
                        <Button
                          colorScheme="blue"
                          size="sm"
                          onClick={connectGoogleCalendar}
                          isLoading={calAuthLoading}
                        >
                          Connect another Google account
                        </Button>
                      </Stack>
                    ) : (
                      /* No connections yet */
                      <HStack>
                        <Button
                          colorScheme="blue"
                          onClick={connectGoogleCalendar}
                          isLoading={calAuthLoading}
                        >
                          Connect Google Calendar
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={checkCalendarConnection}
                          isDisabled={calAuthLoading}
                        >
                          Recheck
                        </Button>
                      </HStack>
                    )}
                  </Stack>

                  {/* Business Name */}
                  <FormControl>
                    <FormLabel fontWeight="500" fontSize="16px" color="#4a5568">
                      Business Name
                    </FormLabel>
                    <Input
                      placeholder="e.g. ABC Dental Clinic"
                      value={formState.business_name}
                      onChange={(e) =>
                        handleFieldChange("business_name", e.target.value)
                      }
                    />
                  </FormControl>

                  {/* Booking link */}
                  <FormControl>
                    <FormLabel fontWeight="500" fontSize="16px" color="#4a5568">
                      Booking / Scheduling Link
                    </FormLabel>
                    <Input
                      placeholder="https://your-booking-link.com"
                      value={formState.booking_link}
                      onChange={(e) =>
                        handleFieldChange("booking_link", e.target.value)
                      }
                    />
                  </FormControl>

                  {/* Review link */}
                  <FormControl>
                    <FormLabel fontWeight="500" fontSize="16px" color="#4a5568">
                      Review Link
                    </FormLabel>
                    <Input
                      placeholder="https://g.page/your-business/review"
                      value={formState.review_link}
                      onChange={(e) =>
                        handleFieldChange("review_link", e.target.value)
                      }
                    />
                  </FormControl>
                </Stack>

                {/* SECTION: Business hours */}
                <Stack spacing={4}>
                  <Box fontWeight="600" fontSize="16px" color="#2d3748">
                    Business Hours (shown to customers)
                  </Box>

                  <FormControl>
                    <FormLabel fontWeight="500" fontSize="16px" color="#4a5568">
                      Business Hours Text
                    </FormLabel>
                    <Textarea
                      rows={4}
                      placeholder={
                        "Example:\n" +
                        "Mon–Fri: 9am – 6pm\n" +
                        "Sat: 10am – 2pm\n" +
                        "Sun: Closed"
                      }
                      value={formState.business_hours}
                      onChange={(e) =>
                        handleFieldChange("business_hours", e.target.value)
                      }
                    />
                    <Box fontSize="12px" color="#718096" mt="4px">
                      This text will be shown to customers and used by AI when
                      talking about your opening hours.
                    </Box>
                  </FormControl>

                  {/* Time Zone */}
                  <FormControl maxW="400px">
                    <FormLabel fontWeight="500" fontSize="16px" color="#4a5568">
                      Business Time Zone
                    </FormLabel>

                    <Select
                      placeholder="Select time zone"
                      value={formState.time_zone}
                      onChange={(e) =>
                        handleFieldChange("time_zone", e.target.value)
                      }
                    >
                      {TIMEZONES.map((tz) => (
                        <option key={tz.value} value={tz.value}>
                          {tz.label}
                        </option>
                      ))}
                    </Select>

                    <Box fontSize="12px" color="#718096" mt="4px">
                      This timezone is used for bookings, Google Calendar
                      events, and AI responses.
                    </Box>
                  </FormControl>
                </Stack>

                {/* SECTION: AI response guidance */}
                <Stack spacing={4}>
                  <Box fontWeight="600" fontSize="16px" color="#2d3748">
                    How should AI talk?
                  </Box>

                  <FormControl>
                    <FormLabel fontWeight="500" fontSize="16px" color="#4a5568">
                      What should AI say to customers?
                    </FormLabel>
                    <Textarea
                      rows={5}
                      placeholder={
                        "Example:\n" +
                        "- We fix cracked iPhone screens in 1 hour.\n" +
                        "- We are located in DHA Lahore.\n" +
                        "- We are open Saturday 10am-2pm.\n" +
                        "- Be polite.\n" +
                        "- Never promise refunds.\n" +
                        "- Offer to book them using our link."
                      }
                      value={formState.faq_context}
                      onChange={(e) =>
                        handleFieldChange("faq_context", e.target.value)
                      }
                    />
                  </FormControl>
                </Stack>

                {/* SECTION: Handoff / Stop rules */}
                <Stack spacing={4}>
                  <Box fontWeight="600" fontSize="16px" color="#2d3748">
                    Handoff Rules (when AI should stop and send to a human)
                  </Box>

                  <FormControl>
                    <FormLabel fontWeight="500" fontSize="16px" color="#4a5568">
                      Keywords that should trigger human takeover
                    </FormLabel>
                    <Input
                      placeholder="refund, angry, complaint, legal, manager"
                      value={handoffKeywordsString}
                      onChange={(e) => onKeywordsChange(e.target.value)}
                    />
                    <Box fontSize="12px" color="#718096" mt="4px">
                      Comma-separated. If customer says any of these, AI will
                      stop.
                    </Box>
                  </FormControl>

                  <FormControl maxW="200px">
                    <FormLabel fontWeight="500" fontSize="16px" color="#4a5568">
                      Max bot replies before handoff
                    </FormLabel>
                    <Input
                      type="number"
                      min="1"
                      value={formState.stop_rules_json.max_turns_before_handoff}
                      onChange={(e) =>
                        handleStopRulesChange(
                          "max_turns_before_handoff",
                          Number(e.target.value)
                        )
                      }
                    />
                    <Box fontSize="12px" color="#718096" mt="4px">
                      After this many back-and-forth messages, AI stops and
                      flags for human.
                    </Box>
                  </FormControl>
                </Stack>

                {/* Save button */}
                <Box display="flex" justifyContent="flex-end">
                  <Button
                    className="slider-success-btn"
                    colorScheme="blue"
                    isLoading={saving}
                    isDisabled={!isDirty || saving}
                    onClick={handleSave}
                  >
                    {saving ? "Saving..." : isDirty ? "Save Settings" : "Saved"}
                  </Button>
                </Box>
              </Stack>
            </CardBody>
          </Card>
        )}
      </Box>
    </Fragment>
  );
}
